/**
 * Safari Cart Component
 * Displays saved custom safaris in the cart
 */

import React, { useState, useEffect } from 'react';
import { ShoppingCartIcon, TrashIcon, CalendarIcon, XMarkIcon, UserIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { CustomSafariCartItem } from '../../types/customSafari';
import { customSafariService } from '../../services/customSafariService';
import { useTheme } from '../../contexts/ThemeContext';
import { userAuthService, User } from '../../services/userAuthService';
import { useAuth } from '../../contexts/AuthContext';
import ItineraryView from './ItineraryView';
import PricingBreakdown from './PricingBreakdown';
import UserAuthModal from './UserAuthModal';
import { useToast } from '../../contexts/ToastContext';

interface SafariCartProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenBuilder?: (tours?: any[]) => void;
}

const SafariCart: React.FC<SafariCartProps> = ({
  isOpen,
  onClose,
  onOpenBuilder
}) => {
  const { showSuccess, showError, showInfo, showWarning } = useToast();

  const { actualTheme } = useTheme();
  const { user: currentUser, isAuthenticated, refreshUser } = useAuth();
  const [cartItems, setCartItems] = useState<CustomSafariCartItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<CustomSafariCartItem | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCartIfAuthenticated();
    }
  }, [isOpen]);

  const loadCartIfAuthenticated = async () => {
    // Don't call refreshUser() here! AuthContext handles session verification.
    // We just rely on the existing state.

    // Require authentication - show auth modal if not authenticated
    if (!currentUser || !isAuthenticated) {
      setShowAuthModal(true);
      setCartItems([]);
      return;
    }

    await loadCart(currentUser.id);
  };

  const loadCart = async (userId: string) => {
    setLoading(true);
    try {
      // Only load cart for authenticated user
      const cart = await customSafariService.getCart(userId);
      setCartItems(cart);
    } catch (error) {
      console.error('Error loading cart:', error);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId: string) => {
    if (!currentUser) return;
    await customSafariService.removeFromCart(itemId, currentUser.id);
    // Cache is cleared in removeFromCart, just reload
    await loadCart(currentUser.id);
    // Trigger cart refresh event
    window.dispatchEvent(new Event('cartUpdated'));
    if (selectedItem?.id === itemId) {
      setSelectedItem(null);
    }
  };

  const handleAuthSuccess = async (user: User) => {
    await refreshUser(); // Refresh auth context
    setShowAuthModal(false);
    if (user) {
      await loadCart(user.id);
    }
  };

  const handleEdit = (item: CustomSafariCartItem) => {

    if (onOpenBuilder) {
      const tours = item.itinerary.tours.map(st => st.tour);
      onOpenBuilder(tours);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-4 md:p-8" style={{ pointerEvents: 'auto' }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-xl transition-opacity"
        onClick={onClose}
        style={{ pointerEvents: 'auto' }}
      />

      {/* Main Panel */}
      <div className={`relative w-full h-[92vh] sm:h-auto max-h-[96vh] sm:max-h-[90vh] sm:max-w-3xl ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'
        } rounded-[2rem] sm:rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
          }`}>
          <div className="flex items-center gap-3 flex-1">
            <ShoppingCartIcon className={`h-6 w-6 ${actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-800'
              }`} />
            <div className="flex-1">
              <h2 className={`text-2xl font-bold ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                }`}>
                Safari Cart
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <p className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>
                  {cartItems.length} saved safari{cartItems.length !== 1 ? 's' : ''}
                </p>
                {currentUser ? (
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${actualTheme === 'dark' ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'
                    }`}>
                    <CheckCircleIcon className="h-3 w-3" />
                    <span>{currentUser.name}</span>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all ${actualTheme === 'dark'
                      ? 'bg-primary-700 hover:bg-primary-600 text-white'
                      : 'bg-primary-800 hover:bg-primary-700 text-white'
                      }`}
                  >
                    <UserIcon className="h-3 w-3" />
                    Sign In to Save
                  </button>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${actualTheme === 'dark'
              ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
              : 'text-neutral-600 hover:bg-neutral-100 hover:text-primary-800'
              }`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!currentUser ? (
            <div className={`text-center py-12 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
              }`}>
              <UserIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">Sign in to view your cart</p>
              <p className="text-sm mb-6">Your custom safaris are saved securely to your account.</p>
              <button
                onClick={() => setShowAuthModal(true)}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${actualTheme === 'dark'
                  ? 'bg-primary-700 hover:bg-primary-600 text-white'
                  : 'bg-primary-800 hover:bg-primary-700 text-white'
                  }`}
              >
                Sign In to View Cart
              </button>
            </div>
          ) : loading ? (
            <div className={`text-center py-12 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
              }`}>
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
              <p>Loading your cart...</p>
            </div>
          ) : cartItems.length === 0 ? (
            <div className={`text-center py-12 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
              }`}>
              <ShoppingCartIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">Your cart is empty</p>
              <p className="text-sm mb-6">Start building your custom safari!</p>
              {onOpenBuilder && (
                <button
                  onClick={() => {
                    onOpenBuilder();
                    onClose();
                  }}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${actualTheme === 'dark'
                    ? 'bg-primary-700 hover:bg-primary-600 text-white'
                    : 'bg-primary-800 hover:bg-primary-700 text-white'
                    }`}
                >
                  Start Building Safari
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className={`rounded-xl border-2 transition-all hover:shadow-lg ${actualTheme === 'dark'
                    ? 'bg-slate-800 border-slate-700 hover:border-primary-600'
                    : 'bg-white border-neutral-200 hover:border-primary-300'
                    } overflow-hidden`}
                >
                  {/* Cart Item Header */}
                  <div className={`p-5 border-b ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
                    }`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-bold text-lg mb-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                          }`}>
                          {item.itinerary.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${actualTheme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-primary-50 text-primary-700'
                            }`}>
                            <span>{item.itinerary.tours.length}</span>
                            <span>tour{item.itinerary.tours.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${actualTheme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-primary-50 text-primary-700'
                            }`}>
                            <span>{item.itinerary.totalDuration}</span>
                            <span>day{item.itinerary.totalDuration !== 1 ? 's' : ''}</span>
                          </div>
                          {item.scheduledDate && (
                            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${actualTheme === 'dark' ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'
                              }`}>
                              <CalendarIcon className="h-3 w-3" />
                              <span>Scheduled</span>
                            </div>
                          )}
                        </div>
                        <div className={`text-xs ${actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                          }`}>
                          Saved {new Date(item.savedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                      <div className="flex items-start gap-2 flex-shrink-0">
                        <button
                          onClick={() => setSelectedItem(selectedItem?.id === item.id ? null : item)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${actualTheme === 'dark'
                            ? 'bg-slate-700 hover:bg-slate-600 text-white'
                            : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                            }`}
                        >
                          {selectedItem?.id === item.id ? 'Hide' : 'View Details'}
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className={`p-2 rounded-lg transition-colors ${actualTheme === 'dark'
                            ? 'text-slate-400 hover:bg-slate-700 hover:text-red-400'
                            : 'text-neutral-600 hover:bg-neutral-100 hover:text-red-600'
                            }`}
                          title="Remove from cart"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className={`flex items-center justify-between mt-4 pt-4 border-t ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
                      }`}>
                      <span className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                        }`}>
                        Total Price
                      </span>
                      <div className={`text-xl font-bold ${actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-800'
                        }`}>
                        {item.itinerary.totalPrice.currency} {item.itinerary.totalPrice.total.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedItem?.id === item.id && (
                    <div className="p-5 space-y-5 bg-gradient-to-b from-transparent to-slate-900/5 dark:to-slate-800/20">
                      <ItineraryView
                        itinerary={item.itinerary}
                        onItineraryUpdate={async (updated) => {
                          // Update cart item
                          const updatedCart = cartItems.map(cartItem =>
                            cartItem.id === item.id
                              ? { ...cartItem, itinerary: updated }
                              : cartItem
                          );
                          setCartItems(updatedCart);
                          setSelectedItem({ ...item, itinerary: updated });
                          // TODO: Update in database
                        }}
                      />
                      <PricingBreakdown pricing={item.itinerary.totalPrice} />
                      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-neutral-200 dark:border-slate-700">
                        <button
                          onClick={() => handleEdit(item)}
                          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${actualTheme === 'dark'
                            ? 'bg-slate-700 hover:bg-slate-600 text-white'
                            : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                            }`}
                        >
                          Edit Safari
                        </button>
                        <button
                          onClick={() => {
                            // TODO: Open scheduling modal
                            showInfo('Scheduling feature coming soon!', 'Feature Unavailable');
                          }}
                          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${actualTheme === 'dark'
                            ? 'bg-primary-700 hover:bg-primary-600 text-white'
                            : 'bg-primary-800 hover:bg-primary-700 text-white'
                            }`}
                        >
                          <CalendarIcon className="h-5 w-5" />
                          Schedule
                        </button>
                        <button
                          onClick={() => {
                            if (!currentUser) {
                              setShowAuthModal(true);
                              return;
                            }
                            // TODO: Open booking form
                            showInfo('Booking feature coming soon!', 'Feature Unavailable');
                          }}
                          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${actualTheme === 'dark'
                            ? 'bg-primary-800 hover:bg-primary-700 text-white'
                            : 'bg-primary-800 hover:bg-primary-700 text-white'
                            }`}
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* User Authentication Modal */}
      {showAuthModal && (
        <UserAuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
          mode="signin"
        />
      )}
    </div>
  );
};

export default SafariCart;

